import React from 'react'
import { WithSession } from '../session/SessionProvider'
import IntegrationsForm from './layout/IntegrationsForm'
import IntegrationsFallback from './layout/IntegrationsFallback'
import FitbitReconnectBanner from './layout/FitbitReconnectBanner'
import FeedbackButtonPanel from '../common/FeedbackButtonPanel'
import FeedbackButton from '../common/FeedbackButton'
import NativeAppStoreLinks from '../native_app/NativeAppStoreLinks'
import { WithFlashMessages } from '../flashmessages/FlashMessageProvider'
import { WithModal } from '../modal/ModalProvider'
// import SwaggerClient from 'swagger-client'
import Swagger from 'swagger-client'
import { MyAxios as axios } from '../MyAxios'
import I18n from 'i18n'
import Routes from '../common/Routes'
import SpinnerWrapper from '../common/SpinnerWrapper'
import { WithBackend } from 'components/backend/BackendProvider'
import { FITBIT_AUTH_VERSION_LEGACY, FITBIT_SERVICE_KEY, MYLAPS_THIALF_KEY } from 'components/common/Constants'
import { WithFeatureFlag } from 'components/feature_flags/FeatureFlagProvider'
import FeatureFlag, {
  APP_STORE_LINKS_FLAG,
  FITBIT_GOOGLE_HEALTH_RECONNECT_BANNER,
  MYLAPS_THIALF,
  PLAY_STORE_LINKS_FLAG
} from 'components/feature_flags/FeatureFlag'
import WithHistory from 'components/common/WithHistory'
import integrationStartUrl from './integrationStartUrl'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

class IntegrationsController extends React.Component {
  constructor (props) {
    super(props)

    // Enable CORS and browser cookies
    Swagger.http.withCredentials = true

    this.client = new Swagger(process.env.INTEGRATIONS_URL + '/api-json')
    this.authHeader = {
      requestInterceptor: (req) => {
        req.headers.Authorization = this.props.sessionToken
        return req
      }
    }

    // The integration with MyLaps works outside of the svc-integrations flow, so we hardcode it here
    this.myLapsThialfIntegration = {
      key: MYLAPS_THIALF_KEY,
      iconUrl: '/images/logo-mylaps-thialf.svg',
      enabled: true,
      canRevoke: true
    }

    this.state = {
      available: [],
      active: {},
      authVersions: {},
      redirect: false,
      fetching: false,
      fetchError: false
    }

    this.errorCardRef = React.createRef()
  }

  isRedirect () {
    return window.location.pathname.includes(Routes.addedIntegration.substring(1))
  }

  redirectParams () {
    const params = new URLSearchParams(window.location.search)
    return {
      status: params.get('status'),
      reason: params.get('reason')
    }
  }

  isRedirectError () {
    return this.isRedirect() && this.redirectParams().status === 'error'
  }

  triggerInitialGathering () {
    if (!this.isRedirect()) return false
    if (this.isRedirectError()) return false

    const endpoint = '/api/v1/jobs/schedule_gathering'
    this.props.flashMessages.push(`${I18n.t('components.integrations.added')} ${I18n.t('components.integrations.delay')}`,
      this.props.flashMessages.duration.SHORT,
      this.props.flashMessages.levels.INFO)
    return axios.post(endpoint, {}, { headers: { Authorization: this.props.sessionToken } })
  }

  callbackErrorCopy () {
    const { reason } = this.redirectParams()
    const reasonScope = `components.integrations.callback.error.reasons.${reason}`
    const hasReasonCopy = reason && I18n.t(`${reasonScope}.title`, { defaultValue: '' }) !== ''
    return {
      title: hasReasonCopy
        ? I18n.t(`${reasonScope}.title`)
        : I18n.t('components.integrations.callback.error.title'),
      description: hasReasonCopy
        ? I18n.t(`${reasonScope}.description`, { defaultValue: I18n.t('components.integrations.callback.error.description') })
        : I18n.t('components.integrations.callback.error.description'),
      flash: hasReasonCopy
        ? I18n.t(`${reasonScope}.flash`, { defaultValue: I18n.t('components.integrations.callback.error.flash') })
        : I18n.t('components.integrations.callback.error.flash')
    }
  }

  notifyCallbackError () {
    if (!this.isRedirectError()) return
    const { flash } = this.callbackErrorCopy()
    this.props.flashMessages.pushErrorMessage(flash)
  }

  focusCallbackError () {
    if (!this.isRedirectError()) return
    const node = this.errorCardRef.current
    if (!node || typeof node.scrollIntoView !== 'function') return
    node.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  renderCallbackError () {
    const { title, description } = this.callbackErrorCopy()

    return (
      <div
        ref={this.errorCardRef}
        className='integrations-callback-error timeline-card'
        data-testid='integrations-callback-error'
        role='alert'
        aria-live='assertive'
        tabIndex={-1}
      >
        <div className='integrations-callback-error__icon'>
          <FontAwesomeIcon icon='exclamation-triangle' size='2x' />
        </div>
        <div className='integrations-callback-error__body'>
          <p className='text-l text-heavy integrations-callback-error__title'>{title}</p>
          <p className='integrations-callback-error__description'>{description}</p>
          <button
            onClick={this.onHandleOkButton.bind(this)}
            id='return-from-integration-button'
            className='waves-effect waves-light text-primary-color background-primary button-primary text-background-color'
          >
            {I18n.t('components.data.close_after_upload')}
          </button>
        </div>
      </div>
    )
  }

  fetchAvailable () {
    return this.client.then(client => (
      client.apis.integration_registration.get_integration_registration()
    ))
  }

  fetchEnabled () {
    return this.client.then(client => (
      client.apis.integration_registration.get_integration_registration_mine({}, this.authHeader)
        .then(response => (
          JSON.parse(response.data).map(e => ({ key: e.key, authVersion: e.authVersion }))
        ))
        .catch(e => {
          if (e.status === 404) {
            // User has no integrations yet
            return []
          } else {
            if (process.env.NODE_ENV !== 'test') console.log(e)
          }
        })

    ))
  }

  async hasTransponder () {
    try {
      const response = await this.props.backend.transponders.show()
      return response.status === 200
    } catch (e) {
      if (e.response?.status !== 404) {
        console.log(e)
        this.props.flashMessages.pushErrorMessage(I18n.t('components.transponder.add_form.errors.unexpected_retrieval'))
      }
      return false
    }
  }

  async handleTransponderRemoval () {
    try {
      await this.props.backend.transponders.delete()
      this.fetch()
    } catch (e) {
      if (e.response) {
        if (e.response.status !== 404) {
          console.log(e)
          this.props.flashMessages.pushErrorMessage(I18n.t('components.transponder.add_form.errors.unexpected_removal'))
        }
      } else if (e.request) {
        console.log(e.request)
        this.props.flashMessages.pushErrorMessage(I18n.t('components.transponder.add_form.errors.unexpected_removal'))
      } else {
        console.log(e)
        this.props.flashMessages.pushErrorMessage(I18n.t('components.transponder.add_form.errors.unexpected_removal'))
      }
    }
  }

  fetch () {
    this.setState({ fetching: true, fetchError: false })
    this.triggerInitialGathering()
    const fetchArray = [this.fetchAvailable(), this.fetchEnabled()]
    const mylapsThialfEnabled = this.props.features?.[MYLAPS_THIALF]
    if (mylapsThialfEnabled) {
      fetchArray.push(this.hasTransponder())
    }
    return Promise.all(fetchArray).then((results) => {
      this.setState({ fetching: false })
      const [available, active, hasTransponder] = results
      const data = JSON.parse(available.data)
      const enabledKeys = (active || []).map(e => e.key)
      const authVersions = (active || []).reduce((acc, e) => {
        if (e.authVersion) acc[e.key] = e.authVersion
        return acc
      }, {})
      const isActive = {}
      data.forEach(item => { isActive[item.key] = enabledKeys.includes(item.key) })
      if (this.props.features?.[MYLAPS_THIALF]) {
        isActive[MYLAPS_THIALF_KEY] = hasTransponder
        data.push(this.myLapsThialfIntegration)
      }
      this.setState({ available: data, active: isActive, authVersions })
    }
    ).catch(e => {
      this.setState({ fetching: false, fetchError: true })
      if (process.env.NODE_ENV !== 'test') console.log(e)
    })
  }

  componentDidMount () {
    this.fetch()
    this.notifyCallbackError()
    this.focusCallbackError()
  }

  handleServiceClick (service) {
    window.location = integrationStartUrl(service.externalUrl)
  }

  handleAddTransponder () {
    this.props.history.push(Routes.transponder.add)
  }

  handleServiceRemoval (service) {
    this.client.then(client => {
      client.apis.integration_registration.delete_integration_registration_mine__key_({ key: service.key }).then(() => {
        this.fetch()
      })
    }).catch(e => {
      if (process.env.NODE_ENV !== 'test') console.error(e)
    })
  }

  onHandleOkButton () {
    this.props.hide()
    window.location = process.env.HOST_URL
  }

  handleFitbitReconnect () {
    const fitbit = this.state.available.find(svc => svc.key === FITBIT_SERVICE_KEY)
    if (!fitbit) return
    this.handleServiceClick(fitbit)
  }

  shouldShowFitbitReconnectBanner () {
    if (!this.props.features?.[FITBIT_GOOGLE_HEALTH_RECONNECT_BANNER]) return false
    if (!this.state.active[FITBIT_SERVICE_KEY]) return false
    return this.state.authVersions[FITBIT_SERVICE_KEY] === FITBIT_AUTH_VERSION_LEGACY
  }

  render () {
    const hasIntegrations = this.state.available.length > 0
    return (
      <>
        {this.shouldShowFitbitReconnectBanner() &&
          <FitbitReconnectBanner onReconnect={this.handleFitbitReconnect.bind(this)} />}
        <SpinnerWrapper ready={!this.state.fetching}>
          {hasIntegrations
            ? (
              <IntegrationsForm
                available={this.state.available}
                active={this.state.active}
                handleServiceClick={this.handleServiceClick.bind(this)}
                handleServiceRemoval={this.handleServiceRemoval.bind(this)}
                handleTransponderRemoval={this.handleTransponderRemoval.bind(this)}
                handleAddTransponder={this.handleAddTransponder.bind(this)}
              />
              )
            : <IntegrationsFallback fetchError={this.state.fetchError} />}
        </SpinnerWrapper>
        <FeatureFlag name={APP_STORE_LINKS_FLAG}>
          <p>
            {I18n.t('components.integrations.nativeAppDownload')}
            <FeatureFlag name={PLAY_STORE_LINKS_FLAG}>
              {' '}{I18n.t('components.integrations.nativeAppDownloadAndroid')}
            </FeatureFlag>
            :
          </p>
          <NativeAppStoreLinks />
        </FeatureFlag>
        <p>{I18n.t('components.integrations.description')}</p>
        <FeedbackButtonPanel>
          <FeedbackButton body={I18n.t('components.integrations.feedback.body')} />
        </FeedbackButtonPanel>
        {this.isRedirect() && this.isRedirectError() && this.renderCallbackError()}
        {this.isRedirect() && !this.isRedirectError() &&
          <>
            <div className='row'>
              <div className='col s12'>
                <p><b>{I18n.t('components.integrations.added')}</b> </p>
                <p><em> {I18n.t('components.integrations.delay')} </em></p>
              </div>
            </div>
            <div className='row'>
              <div className='col s12'>
                <button onClick={this.onHandleOkButton.bind(this)} id='return-from-integration-button' className='waves-effect waves-light text-primary-color background-primary button-primary text-background-color'>{I18n.t('components.data.close_after_upload')}</button>
              </div>
            </div>
          </>}
      </>
    )
  }
}

export default WithHistory(WithFeatureFlag(WithModal(WithFlashMessages(WithBackend(WithSession(IntegrationsController))))))
